// PatchAgentsTable.js
import React, { useState } from "react";
import {
  Button, Modal, ModalHeader, ModalBody, ModalFooter,
  Nav, NavItem, NavLink, TabContent, TabPane, Alert, Badge
} from "reactstrap";
import FeatherIcon from "feather-icons-react";

const PatchAgentsTable = ({ agents }) => {
  const [detailsModal, setDetailsModal] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [detailsTab, setDetailsTab] = useState('pending');

  const openDetailsModal = (agent) => {
    if (!agent.patch_data?.data?.data) {
      alert('Patch data not available for this agent yet. Please wait for data to load or click Refresh.');
      return;
    }

    setSelectedAgent(agent);
    setDetailsTab('pending');
    setDetailsModal(true);
  };

  const closeDetailsModal = () => {
    setDetailsModal(false);
    setSelectedAgent(null);
    setDetailsTab('pending');
  };

  const getPendingPatches = (agent) => {
    if (!agent) return [];
    return agent?.patch_data?.data?.data?.updates || [];
  };

  const getInstalledPatches = (agent) => {
    if (!agent) return [];
    return agent?.patch_data?.data?.data?.installed_updates || [];
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    try {
      const date = new Date(dateString);
      const now = new Date();
      const diffMins = Math.floor((now - date) / 60000);
      const diffHours = Math.floor((now - date) / 3600000);
      if (diffMins < 60) return `${diffMins}m ago`;
      if (diffHours < 24) return `${diffHours}h ago`;
      return date.toLocaleDateString();
    } catch {
      return 'Unknown';
    }
  };

  return (
    <>
      {agents.length === 0 ? (
        <div className="text-center p-5">
          <FeatherIcon icon="server" size={48} className="text-muted mb-3" />
          <p className="text-muted mb-0">
            No devices found. Install agents on endpoints.
          </p>
        </div>
      ) : (
        <div className="table-responsive">
          <table className="table table-hover align-middle mb-0">
            <thead style={{ backgroundColor: 'var(--bs-tertiary-bg)', color: 'var(--bs-body-color)' }}>
              <tr>
                <th className="fw-bold border-0">Hostname</th>
                <th className="fw-bold border-0">IP Address</th>
                <th className="fw-bold border-0">Operating System</th>
                <th className="fw-bold border-0">Status</th>
                <th className="fw-bold border-0">Installed</th>
                <th className="fw-bold border-0">Pending</th>
                <th className="fw-bold border-0">Compliance</th>
                <th className="fw-bold border-0">Last Checked</th>
                <th className="fw-bold border-0">Actions</th>
              </tr>
            </thead>
            <tbody>
              {agents.map((agent) => {
                const patchData = agent.patch_data?.data?.data || {};
                const installedCount = patchData.installed_updates?.length || 0;
                const pendingCount = patchData.updates?.length || 0;
                const complianceStatus =
                  pendingCount === 0
                    ? "compliant"
                    : pendingCount <= 3
                      ? "warning"
                      : "critical";

                const compliancePercentage =
                  pendingCount === 0
                    ? 100
                    : Math.min(
                      (installedCount / (installedCount + pendingCount || 1)) * 100,
                      100
                    );

                return (
                  <tr key={agent.agent_id} >
                    <td className="fw-semibold" style={{ color: 'var(--bs-body-color)' }}>
                      <FeatherIcon icon="monitor" size={16} className="text-primary me-2" />
                      {agent.hostname}
                    </td>
                    <td className="text-muted">{agent.ip_address}</td>
                    <td className="text-muted">{agent.os_info}</td>

                    <td className="fw-semibold">
                      <span
                        className={`text-${agent.status === "online" ? "success" : "danger"
                          }`}
                      >
                        {agent.status === "online" ? "Online" : "Offline"}
                      </span>
                    </td>

                    <td className="text-mute fw-medium">{installedCount}</td>
                    <td
                      className={`fw-medium text-${pendingCount === 0
                          ? "primary"
                          : pendingCount <= 3
                            ? "warning"
                            : "danger"
                        }`}
                    >
                      {pendingCount}
                    </td>

                    <td>
                      <div className="d-flex flex-column align-items-start">
                        <span
                          className="fw-semibold me-2"
                          style={{
                            color: complianceStatus === "compliant"
                              ? "#57a761ff"
                              : complianceStatus === "warning"
                                ? "#d9f84c"
                                : "#dc3545"
                          }}
                        >
                          {complianceStatus.charAt(0).toUpperCase() + complianceStatus.slice(1)}
                        </span>
                        <div className="progress w-100 mt-1" style={{ height: "6px", maxWidth: "120px" }}>
                          <div
                            className="progress-bar"
                            role="progressbar"
                            style={{
                              width: `${compliancePercentage}%`,
                              backgroundColor: complianceStatus === "compliant"
                                ? "#71816d"   
                                : complianceStatus === "warning"
                                  ? "#b7c288"   
                                  : "#848886"  
                            }}
                          ></div>
                        </div>
                      </div>
                    </td>


                    <td className="text-muted">
                      <FeatherIcon icon="clock" size={14} className="me-1" />
                      {formatDate(agent.last_check)}
                    </td>

                    <td>
                      <Button
                        color="outline-primary"
                        size="sm"
                        onClick={() => openDetailsModal(agent)}
                      >
                        <FeatherIcon icon="eye" size={14} className="me-1" />
                        View
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Patch Details Modal */}
      <Modal isOpen={detailsModal} toggle={closeDetailsModal} size="lg">
        <ModalHeader toggle={closeDetailsModal} style={{ backgroundColor: 'var(--bs-secondary-bg)', color: 'var(--bs-body-color)', borderBottom: '1px solid var(--bs-border-color)' }}>
          Patch Details - {selectedAgent?.hostname}
        </ModalHeader>
        <ModalBody style={{ backgroundColor: 'var(--bs-body-bg)', color: 'var(--bs-body-color)' }}>
          {selectedAgent && (
            <>
              <div className="mb-3 p-3 rounded" style={{ backgroundColor: 'var(--bs-secondary-bg)' }}>
                <div className="row">
                  <div className="col-6">
                    <small className="text-muted">System:</small>
                    <div className="fw-bold">{selectedAgent.os_info || 'Unknown'}</div>
                  </div>
                  <div className="col-6">
                    <small className="text-muted">Last Check:</small>
                    <div className="fw-bold">{formatDate(selectedAgent.last_check)}</div>
                  </div>
                </div>
              </div>

              <Nav tabs className="mb-3" style={{ borderBottom: '1px solid var(--bs-border-color)' }}>
                <NavItem>
                  <NavLink
                    className={detailsTab === 'pending' ? 'active' : ''}
                    onClick={() => setDetailsTab('pending')}
                    style={{
                      cursor: 'pointer',
                      backgroundColor: detailsTab === 'pending' ? 'var(--bs-body-bg)' : 'transparent',
                      color: detailsTab === 'pending' ? 'var(--bs-primary)' : 'var(--bs-secondary-color)',
                      border: 'none',
                      borderBottom: detailsTab === 'pending' ? '2px solid var(--bs-primary)' : 'none'
                    }}
                  >
                    <FeatherIcon icon="alert-circle" size="14" className="me-1" style={{ display: 'inline' }} />
                    Pending ({getPendingPatches(selectedAgent).length})
                  </NavLink>
                </NavItem>
                <NavItem>
                  <NavLink
                    className={detailsTab === 'installed' ? 'active' : ''}
                    onClick={() => setDetailsTab('installed')}
                    style={{
                      cursor: 'pointer',
                      backgroundColor: detailsTab === 'installed' ? 'var(--bs-body-bg)' : 'transparent',
                      color: detailsTab === 'installed' ? 'var(--bs-primary)' : 'var(--bs-secondary-color)',
                      border: 'none',
                      borderBottom: detailsTab === 'installed' ? '2px solid var(--bs-primary)' : 'none'
                    }}
                  >
                    <FeatherIcon icon="check-circle" size="14" className="me-1" style={{ display: 'inline' }} />
                    Installed ({getInstalledPatches(selectedAgent).length})
                  </NavLink>
                </NavItem>
              </Nav>

              <TabContent activeTab={detailsTab}>
                <TabPane tabId="pending">
                  {getPendingPatches(selectedAgent).length > 0 ? (
                    <>
                      <Alert color="primary" className="mb-3">
                        <FeatherIcon icon="download-cloud" size="16" className="me-2" style={{ display: 'inline' }} />
                        <strong>{getPendingPatches(selectedAgent).length}</strong> patch(es) available for installation
                      </Alert>

                      <div className="table-responsive" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                        <table className="table table-sm">
                          <thead className="sticky-top" style={{ backgroundColor: 'var(--bs-tertiary-bg)' }}>
                            <tr>
                              <th className="border-0">Title</th>
                              <th className="border-0">KB</th>
                              <th className="border-0">Type</th>
                              <th className="border-0">Size</th>
                            </tr>
                          </thead>
                          <tbody>
                            {getPendingPatches(selectedAgent).map((patch, index) => (
                              <tr key={index} style={{ borderBottom: '1px solid var(--bs-border-color)' }}>
                                <td>
                                  <div className="fw-medium">{patch.title || patch.Title || 'Windows Update'}</div>
                                  {patch.description && (
                                    <small className="text-muted d-block">
                                      {patch.description.length > 80
                                        ? `${patch.description.substring(0, 80)}...`
                                        : patch.description}
                                    </small>
                                  )}
                                </td>
                                <td>
                                  <code style={{ backgroundColor: 'var(--bs-secondary-bg)', color: 'var(--bs-code-color)' }}>
                                    {patch.kb || patch.KB || 'N/A'}
                                  </code>
                                </td>
                                <td>
                                  <Badge color="primary" className="bg-transparent border" style={{ border: "1px solid #d8800c" }}>
                                    {patch.type || patch.Type || 'Update'}
                                  </Badge>
                                </td>
                                <td>
                                  <small>{patch.size_mb || patch.SizeMB || 'Unknown'} MB</small>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </>
                  ) : (
                    <Alert color="success">
                      <div className="text-center py-3">
                        <FeatherIcon icon="check-circle" size="48" className="text-success mb-3" />
                        <h6>No Pending Patches</h6>
                        <p className="text-muted mb-0">System is up to date!</p>
                      </div>
                    </Alert>
                  )}
                </TabPane>

                <TabPane tabId="installed">
                  {getInstalledPatches(selectedAgent).length > 0 ? (
                    <>
                      <Alert color="success" className="mb-3">
                        <FeatherIcon icon="check-circle" size="16" className="me-2" style={{ display: 'inline' }} />
                        <strong>{getInstalledPatches(selectedAgent).length}</strong> patch(es) installed
                      </Alert>

                      <div className="table-responsive" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                        <table className="table table-sm">
                          <thead className="sticky-top" style={{ backgroundColor: 'var(--bs-tertiary-bg)' }}>
                            <tr>
                              <th className="border-0">Title</th>
                              <th className="border-0">KB</th>
                              <th className="border-0">Type</th>
                              <th className="border-0">Installed Date</th>
                            </tr>
                          </thead>
                          <tbody>
                            {getInstalledPatches(selectedAgent).map((patch, index) => (
                              <tr key={index} style={{ borderBottom: '1px solid var(--bs-border-color)' }}>
                                <td>
                                  <div className="fw-medium">{patch.title || patch.Title || patch.Description || 'Windows Update'}</div>
                                </td>
                                <td>
                                  <code style={{ backgroundColor: 'var(--bs-secondary-bg)', color: 'var(--bs-code-color)' }}>
                                    {patch.kb || patch.KB || patch.HotFixID || 'N/A'}
                                  </code>
                                </td>
                                <td>
                                  <Badge color="success">
                                    {patch.type || patch.Type || patch.Classification || 'Update'}
                                  </Badge>
                                </td>
                                <td>
                                  <small>
                                    {patch.date_installed || patch.InstalledOn
                                      ? new Date(patch.date_installed || patch.InstalledOn).toLocaleDateString()
                                      : 'Unknown'}
                                  </small>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </>
                  ) : (
                    <Alert color="warning">
                      <div className="text-center py-3">
                        <FeatherIcon icon="warning" size="48" className="text-muted mb-3" />
                        <h6>No Installed Patches</h6>
                        <p className="text-muted mb-0">No installation history available</p>
                      </div>
                    </Alert>
                  )}
                </TabPane>
              </TabContent>
            </>
          )}
        </ModalBody>
        <ModalFooter style={{ backgroundColor: 'var(--bs-body-bg)', borderTop: '1px solid var(--bs-border-color)' }}>
          <Button color="primary" onClick={closeDetailsModal} className="btn btn-outline-primary">
            Close
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
};

export default PatchAgentsTable;